import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {StringSchema} from 'joi';

import {
  LoadingButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewNumberInputField,
  NewSelectField,
  NewTextInputField,
  showModal,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {LeftButton} from 'src/components/primitives';
import {EResellerIds, useReseller} from 'src/containers/User';
import {ISO3CountryCodeList} from 'src/utils';
import {joiResolver} from 'src/utils/validator';

import {useGetCompanyDetailsQuery, useSaveCompanyDetailsMutation} from '../api';
import {EResidentCountry, TCompanyDetails} from '../definitions';
import {getActionName, getResidentCountry} from '../utils';
import PaymentDetailsModal from './PaymentDetailsModal';

type TProps = {
  closeModal: TEmptyFunction;
  isNeedFillPaymentDetails?: boolean;
};

export const getCompanyDetailsSchema =
  (residentCountry: EResidentCountry, {hasOgrn}: {hasOgrn: boolean}): TCreateValidateSchema =>
  (joi, mixed) => {
    const common: Partial<Record<keyof TCompanyDetails, StringSchema>> = {
      country: joi.string().required(),
      taxNumber: joi.string().required(),
      companyName: joi.string().required(),
      postAddress: joi.string().required(),
      legalAddress: joi.string().required(),
    };

    switch (residentCountry) {
      case EResidentCountry.RUS:
        const rus = {
          ...common,
          taxNumber: mixed.tin.required(),
          siteAddress: joi.string().required(),
          kpp: mixed.kpp.when('taxNumber', {
            is: joi.string().length(10),
            then: joi.required(),
          }),
        };

        if (hasOgrn) {
          rus.ogrn = joi
            .string()
            .length(13)
            .required()
            .when('taxNumber', {
              is: joi.string().min(12).exist(),
              then: joi.string().length(15).required(),
            });
        }

        return joi.object(rus);

      case EResidentCountry.BLR:
        return joi.object({
          ...common,
          siteAddress: joi.string(),
        });
      case EResidentCountry.FOREIGN:
        const foreign = {
          ...common,
          authorizedPersonName: joi.string().required(),
          registrationEmail: joi.string().required(),
          vatNumber: joi.string().required(),
        };

        if (hasOgrn) {
          foreign.ogrn = joi.string().max(100);
        }

        return joi.object(foreign);
    }
  };

type TDetailsProps = {
  hasTaxNumber: boolean;
  hasOGRN: boolean;
  isShowOGRN: boolean;
};

const ForeignCompanyDetails = React.memo<TDetailsProps>(({hasTaxNumber, hasOGRN, isShowOGRN}) => {
  const {t} = useTranslation();

  return (
    <>
      {isShowOGRN && (
        <NewTextInputField name="ogrn" label={t('CompanyDetails:ogrn')} readOnly={hasOGRN} />
      )}
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:taxNumber')}
        readOnly={hasTaxNumber}
      />
      <NewTextInputField
        name="authorizedPersonName"
        label={t('CompanyDetails:authorizedPersonName')}
      />
      <NewTextInputField name="registrationEmail" label={t('CompanyDetails:registrationEmail')} />
      <NewTextInputField name="vatNumber" label={t('CompanyDetails:vatNumber')} />
    </>
  );
});

ForeignCompanyDetails.displayName = 'ForeignCompanyDetails';

const RusCompanyDetails = React.memo<TDetailsProps>(({hasTaxNumber, hasOGRN, isShowOGRN}) => {
  const {t} = useTranslation();

  return (
    <>
      {isShowOGRN && (
        <NewNumberInputField name="ogrn" label={t('CompanyDetails:ogrn')} readOnly={hasOGRN} />
      )}
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:taxNumber')}
        readOnly={hasTaxNumber}
      />
      <NewTextInputField name="kpp" label={t('CompanyDetails:kpp')} />
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewTextInputField name="siteAddress" label={t('CompanyDetails:siteAddress')} />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
    </>
  );
});

RusCompanyDetails.displayName = 'RusCompanyDetails';

const BlrCompanyDetails = React.memo<{hasTaxNumber: boolean}>(({hasTaxNumber}) => {
  const {t} = useTranslation();

  return (
    <>
      <NewTextInputField name="companyName" label={t('CompanyDetails:companyName')} />
      <NewNumberInputField
        name="taxNumber"
        label={t('CompanyDetails:blrTaxNumber')}
        readOnly={hasTaxNumber}
      />
      <NewTextInputField name="siteAddress" label={t('CompanyDetails:siteAddress')} />
      <NewTextInputField name="postAddress" label={t('CompanyDetails:postAddress')} />
      <NewTextInputField name="legalAddress" label={t('CompanyDetails:legalAddress')} />
    </>
  );
});

BlrCompanyDetails.displayName = 'BlrCompanyDetails';

const CompanyDetailsModal = React.memo<TProps>(({closeModal, isNeedFillPaymentDetails}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const isOSKReseller = useReseller(EResellerIds.OSK_ID);

  const {data: companyDetails} = useGetCompanyDetailsQuery(supportTenantId);
  const [saveCompanyDetails, {isSuccess}] = useSaveCompanyDetailsMutation();

  const hasTaxNumber = !!companyDetails?.taxNumber;
  const hasOGRN = !!companyDetails?.ogrn;
  const companyDetailsAction = React.useMemo(() => getActionName(companyDetails), [companyDetails]);

  const resolver = React.useCallback(
    (values, context, options) =>
      joiResolver(
        getCompanyDetailsSchema(getResidentCountry(values.country), {hasOgrn: isOSKReseller}),
      )(values, context, options),
    [],
  );

  useOnChange(() => {
    if (isSuccess) {
      if (isNeedFillPaymentDetails) {
        showModal((closePaymentDetailsModal) => (
          <PaymentDetailsModal closeModal={closePaymentDetailsModal} />
        ));
      }

      closeModal();
    }
  }, [isSuccess]);

  const onSubmit = React.useCallback(
    (values) => saveCompanyDetails({supportTenantId, data: values}),
    [supportTenantId, saveCompanyDetails],
  );

  const methods = useForm({
    mode: 'all',
    defaultValues: companyDetails,
    resolver,
  });

  const residentCountry = getResidentCountry(methods.watch('country'));

  useOnChange(() => methods.reset(companyDetails), [companyDetails]);

  return (
    <Modal width="582px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ModalHeader>
            {t(`CompanyDetails:detailsAction.${companyDetailsAction}`, {
              detailsName: t('CompanyDetails:modalTitle.companyDetails'),
            })}
          </ModalHeader>
          <ModalContent>
            <NewSelectField
              isValueCompareInSearch
              name="country"
              label={t(`CompanyDetails:country`)}
              options={ISO3CountryCodeList.map((code) => ({
                label: t(`Country:iso3.${code}`),
                value: code,
              }))}
            />
            {residentCountry === EResidentCountry.FOREIGN && (
              <ForeignCompanyDetails
                hasTaxNumber={hasTaxNumber}
                hasOGRN={hasOGRN}
                isShowOGRN={isOSKReseller}
              />
            )}
            {residentCountry === EResidentCountry.RUS && (
              <RusCompanyDetails
                hasTaxNumber={hasTaxNumber}
                hasOGRN={hasOGRN}
                isShowOGRN={isOSKReseller}
              />
            )}
            {residentCountry === EResidentCountry.BLR && (
              <BlrCompanyDetails hasTaxNumber={hasTaxNumber} />
            )}
            <ModalActions>
              <LeftButton onClick={closeModal} data-selector="Common:button.cancel">
                {t('Common:button.cancel')}
              </LeftButton>
              <LoadingButton appearance="primary" type="submit" data-selector="Common:button.save">
                {t('Common:button.save')}
              </LoadingButton>
            </ModalActions>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
});

export default CompanyDetailsModal;
